import { Fragment, useState } from 'react';
import { enqueueSnackbar } from 'notistack';

import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import Menu from '@mui/material/Menu';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import TableRow from '@mui/material/TableRow';
import MenuItem from '@mui/material/MenuItem';
import Collapse from '@mui/material/Collapse';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import CardHeader from '@mui/material/CardHeader';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import TableContainer from '@mui/material/TableContainer';

import { paths } from 'src/routes/paths';
import { RouterLink } from 'src/routes/components';

import { useOrgTenant } from 'src/hooks/auth/use-org-tenant';

import { fDate, fDateTime } from 'src/utils/format-time';

import { useTranslate } from 'src/locales';
import { PresentationExport, PresentationSummary } from 'src/services/presentations/presentations.types';
import {
  useDeletePresentationMutation,
  useDuplicatePresentationMutation,
  useGetCandidatePresentationsQuery,
  useGetPresentationExportDownloadMutation,
} from 'src/services/presentations/presentations.service';

import Label from 'src/components/label';
import Iconify from 'src/components/iconify';
import { ConfirmDialog } from 'src/components/custom-dialog';

import { useFeatureFlags } from 'src/sections/billing/usage/hooks/use-feature-flags';

import { TenantType } from 'src/types/enums';
import { FeatureType } from 'src/types/subscription.types';

import { downloadFromUrl } from '../hooks/use-presentation-preview';
import CreatePresentationDialog from '../modals/create-presentation-dialog';

// ----------------------------------------------------------------------

interface Props {
  /** The OrganizationCandidate id. */
  candidateId: string;
  candidateName?: string;
}

const person = (p?: { first_name: string; last_name: string } | null) => (p ? `${p.first_name} ${p.last_name}`.trim() : '');

/**
 * A candidate's presentations with their export history (JT1-1886), on the Documents tab. Exports
 * live here, not with the uploaded documents: they are copies sent to clients, not the candidate's files.
 */
export default function CandidatePresentationsCard(props: Props) {
  const tenantType = useOrgTenant();
  const { featureEnabled } = useFeatureFlags();
  // Only agencies with the builder see the card, so callers can always render it.
  if (tenantType !== TenantType.Recruiter || !featureEnabled(FeatureType.CANDIDATE_PRESENTATIONS_ENABLED)) {
    return null;
  }
  return <PresentationsCard {...props} />;
}

function PresentationsCard({ candidateId, candidateName }: Props) {
  const { t } = useTranslate();
  const { currentData: presentations = [], isLoading } = useGetCandidatePresentationsQuery(candidateId);
  const [duplicate] = useDuplicatePresentationMutation();
  const [remove, { isLoading: deleting }] = useDeletePresentationMutation();
  const [getDownload] = useGetPresentationExportDownloadMutation();

  const [creating, setCreating] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ anchor: HTMLElement; presentation: PresentationSummary } | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<PresentationSummary | null>(null);

  const download = async (presentation: PresentationSummary, item: PresentationExport) => {
    try {
      const { url } = await getDownload({ id: presentation.id, exportId: item.id }).unwrap();
      downloadFromUrl(url, item.file_name);
    } catch {
      enqueueSnackbar(t('presentations.list.download_failed'), { variant: 'error' });
    }
  };

  const handleDuplicate = async (presentation: PresentationSummary) => {
    setMenu(null);
    try {
      await duplicate({ id: presentation.id, candidateId }).unwrap();
      enqueueSnackbar(t('presentations.list.duplicated'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('presentations.list.action_failed'), { variant: 'error' });
    }
  };

  const handleDelete = async () => {
    if (!confirmDelete) return;
    try {
      await remove({ id: confirmDelete.id, candidateId }).unwrap();
      enqueueSnackbar(t('presentations.list.deleted'), { variant: 'success' });
    } catch {
      enqueueSnackbar(t('presentations.list.action_failed'), { variant: 'error' });
    }
    setConfirmDelete(null);
  };

  return (
    <Card>
      <CardHeader
        title={t('presentations.list.title')}
        subheader={t('presentations.list.subheader')}
        action={(
          <Button variant="soft" color="primary" startIcon={<Iconify icon="mingcute:add-line" />} onClick={() => setCreating(true)}>
            {t('presentations.actions.create')}
          </Button>
        )}
      />

      {!isLoading && !presentations.length ? (
        <Typography variant="body2" sx={{ color: 'text.secondary', p: 3 }}>{t('presentations.list.empty')}</Typography>
      ) : (
        <TableContainer sx={{ mt: 3, overflowX: 'auto' }}>
          <Table sx={{ minWidth: 800 }}>
            <TableHead>
              <TableRow>
                <TableCell>{t('presentations.list.columns.title')}</TableCell>
                <TableCell>{t('presentations.list.columns.type')}</TableCell>
                <TableCell>{t('presentations.list.columns.job')}</TableCell>
                <TableCell>{t('presentations.list.columns.edited')}</TableCell>
                <TableCell>{t('presentations.list.columns.latest_export')}</TableCell>
                <TableCell width={150} />
              </TableRow>
            </TableHead>
            <TableBody>
              {presentations.map((presentation) => (
                <Fragment key={presentation.id}>
                  <TableRow hover selected={expanded === presentation.id}>
                    <TableCell>
                      <Typography variant="subtitle2">{presentation.title}</Typography>
                    </TableCell>
                    <TableCell>{t(`presentations.kinds.${presentation.kind}.label`)}</TableCell>
                    <TableCell sx={{ color: presentation.job ? 'text.primary' : 'text.secondary' }}>
                      {presentation.job?.title ?? t('presentations.list.not_linked')}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">{fDate(presentation.updated_at)}</Typography>
                      <Typography variant="caption" sx={{ color: 'text.secondary' }}>{person(presentation.updated_by)}</Typography>
                    </TableCell>
                    <TableCell>
                      {presentation.latest_export ? (
                        <Stack direction="row" spacing={0.75} alignItems="center" flexWrap="wrap">
                          <span>{`${presentation.latest_export.format.toUpperCase()}, v${presentation.latest_export.presentation_version}`}</span>
                          {presentation.latest_export.anonymised && <Label variant="soft" color="primary">{t('presentations.list.anonymised')}</Label>}
                        </Stack>
                      ) : (
                        <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('presentations.list.not_exported')}</Typography>
                      )}
                    </TableCell>
                    <TableCell align="right">
                      <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                        {presentation.export_count > 0 && (
                          <IconButton
                            size="small"
                            aria-label={t('presentations.list.history')}
                            aria-expanded={expanded === presentation.id}
                            onClick={() => setExpanded(expanded === presentation.id ? null : presentation.id)}
                          >
                            <Iconify icon={expanded === presentation.id ? 'eva:arrow-ios-upward-fill' : 'eva:arrow-ios-downward-fill'} />
                          </IconButton>
                        )}
                        <Button size="small" component={RouterLink} href={paths.dashboard.candidates.presentation(candidateId, presentation.id)}>
                          {t('common.open')}
                        </Button>
                        <IconButton size="small" aria-label={t('presentations.list.more')} onClick={(event) => setMenu({ anchor: event.currentTarget, presentation })}>
                          <Iconify icon="eva:more-vertical-fill" />
                        </IconButton>
                      </Stack>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell colSpan={6} sx={{ py: 0, borderBottom: expanded === presentation.id ? undefined : 'none' }}>
                      <Collapse in={expanded === presentation.id} unmountOnExit>
                        <Box sx={{ py: 2, pl: 2 }}>
                          <Typography variant="subtitle2" sx={{ mb: 1 }}>{t('presentations.list.history')}</Typography>
                          {presentation.exports.map((item) => (
                            <Stack key={item.id} direction="row" spacing={2} alignItems="center" sx={{ py: 0.75, borderTop: (theme) => `1px dashed ${theme.palette.divider}` }}>
                              <Typography variant="body2" sx={{ minWidth: 90 }}><strong>{`v${item.presentation_version}`}</strong>{` · ${item.format.toUpperCase()}`}</Typography>
                              <Box sx={{ minWidth: 110 }}>
                                <Label variant="soft" color={item.anonymised ? 'primary' : 'default'}>
                                  {item.anonymised ? t('presentations.list.anonymised') : t('presentations.list.named')}
                                </Label>
                              </Box>
                              <Typography variant="body2" sx={{ color: 'text.secondary', flex: 1 }}>
                                {[fDateTime(item.created_at), person(item.created_by), item.findings_acknowledged ? t('presentations.list.checks_confirmed') : null].filter(Boolean).join(' · ')}
                              </Typography>
                              <Button size="small" color="inherit" startIcon={<Iconify icon="eva:download-fill" />} onClick={() => download(presentation, item)}>
                                {t('common.download')}
                              </Button>
                            </Stack>
                          ))}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <Menu open={!!menu} anchorEl={menu?.anchor} onClose={() => setMenu(null)}>
        <MenuItem onClick={() => menu && handleDuplicate(menu.presentation)}>
          <Iconify icon="solar:copy-bold" sx={{ mr: 1 }} />
          {t('presentations.list.duplicate')}
        </MenuItem>
        <MenuItem onClick={() => { setConfirmDelete(menu?.presentation ?? null); setMenu(null); }} sx={{ color: 'error.main' }}>
          <Iconify icon="solar:trash-bin-trash-bold" sx={{ mr: 1 }} />
          {t('common.delete')}
        </MenuItem>
      </Menu>

      <ConfirmDialog
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        title={t('presentations.list.delete_title')}
        content={t('presentations.list.delete_body')}
        action={<Button variant="contained" color="error" disabled={deleting} onClick={handleDelete}>{t('common.delete')}</Button>}
      />

      {creating && <CreatePresentationDialog open onClose={() => setCreating(false)} candidateId={candidateId} candidateName={candidateName} />}
    </Card>
  );
}
